/**
 * How a position VALUE renders — the one place the three states are told apart.
 *
 * Rule 14 (F0 D3): raw, `'•••'` and `null` are three DIFFERENT facts —
 * visible, restricted, and absent (never captured, or purged under the
 * per-country retention window) — and a register that rendered any two of
 * them alike would be lying about one. The API decided WHICH state per row
 * per caller; this module only names what arrived. Nothing here consults a
 * permission: branching on `can()` would be a second implementation of the
 * server's Rule-14 decision, wrong today for self rows (self is raw even
 * without `field.location.view`) and wrong forever the day the matrix moves.
 */

import { MASKED_POSITION } from './api';
import type {
  ArrivalAgreement,
  ArrivalDistanceBand,
  MaskedDistance,
  MaskedPosition,
  PositionView,
} from './api';

export type PositionState =
  | { kind: 'raw'; view: PositionView }
  | { kind: 'restricted' }
  | { kind: 'absent' };

export function classifyPosition(value: MaskedPosition): PositionState {
  if (value === null) return { kind: 'absent' };
  if (value === MASKED_POSITION) return { kind: 'restricted' };
  return { kind: 'raw', view: value };
}

/**
 * "19.0760, 72.8777" — the DECIMAL strings trimmed to 4 dp (~11 m, coarser
 * than any phone's fix) for the register cell; the full stored precision stays
 * in the cell's `title`. Display only: nothing recomputes from this.
 */
export function formatCoordinates(view: PositionView): string {
  return `${trimDecimal(view.lat, 4)}, ${trimDecimal(view.lng, 4)}`;
}

/** "±12 m" / "±1.2 km", or '' when the device reported no accuracy. */
export function formatAccuracy(view: PositionView): string {
  if (view.accuracy_m === null) return '';
  const meters = Number(view.accuracy_m);
  if (!Number.isFinite(meters)) return '';
  if (meters >= 1000) return `±${(meters / 1000).toFixed(1)} km`;
  return `±${Math.round(meters)} m`;
}

/**
 * "120 m" / "38.6 km" — RAW arrival metres, for the seats that receive them.
 *
 * ⚠ NOT THE FUNCTION A SCREEN SHOULD CALL WITH A WIRE VALUE (v17 F-1). The wire
 * field is `MaskedDistance` and can be `'•••'`; `Number('•••')` is `NaN`, which
 * this returns `''` for — silently dropping the fact that the visit HAPPENED
 * 40 km away. Screens call {@link formatArrivalDistance}, which is handed the
 * band as well and therefore always has something honest to say. This one stays
 * exported because it is the raw-metre formatter that function delegates to.
 */
export function formatDistanceM(decimal: string | null): string {
  if (decimal === null) return '';
  const meters = Number(decimal);
  if (!Number.isFinite(meters)) return '';
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

/* ───────────────────────────────────────── arrival distance, v17 F-1 ── */

/**
 * The three states an arrival distance arrives in — the same shape, and the
 * same server decision, as {@link PositionState}. `banded` is the state that
 * did not exist before F-1: the caller may not have the metres, but the coarse
 * band is theirs and it is the whole point of the register.
 */
export type DistanceState =
  | { kind: 'raw'; metres: string; band: ArrivalDistanceBand | null }
  | { kind: 'banded'; band: ArrivalDistanceBand }
  | { kind: 'absent' };

export function classifyDistance(
  value: MaskedDistance,
  band: ArrivalDistanceBand | null,
): DistanceState {
  if (value === null) return { kind: 'absent' };
  if (value === MASKED_POSITION) {
    // Masked WITHOUT a band should be impossible — the server computes the band
    // from the same column it masks. Render 'absent' rather than an empty pill
    // if it ever happens: a marker with nothing to say is worse than silence.
    return band === null ? { kind: 'absent' } : { kind: 'banded', band };
  }
  return { kind: 'raw', metres: value, band };
}

/**
 * The band's LABEL. The boundaries are closed on the upper side and the words
 * say so — "within 250 m" includes 250 m exactly. "over 5 km" is deliberately
 * plain: it is the phrase that has to make a 40 km visit read as a finding to
 * somebody scanning a register.
 */
export const ARRIVAL_BAND_LABELS: Record<ArrivalDistanceBand, string> = {
  within_250m: 'within 250 m',
  within_5km: '250 m – 5 km',
  beyond_5km: 'over 5 km',
};

/**
 * What a register cell or a timeline sentence actually prints — ONE call, so no
 * screen has to know which of the three states it got. `''` means "say nothing"
 * and only ever comes from the honestly-absent case.
 */
export function formatArrivalDistance(
  value: MaskedDistance,
  band: ArrivalDistanceBand | null,
): string {
  const state = classifyDistance(value, band);
  if (state.kind === 'absent') return '';
  if (state.kind === 'banded') return ARRIVAL_BAND_LABELS[state.band];
  return formatDistanceM(state.metres);
}

/* ─────────────────────────────────────── arrival agreement, v17 ruling 6 ── */

/**
 * The FOUR states an arrival agreement arrives in.
 *
 * `restricted` is the arm ruling 6 added, and it exists as its own state for one
 * reason: without it a screen falls through to `undeterminable`, and the copy
 * there says "one side had no coordinates at arrival" — a sentence that is
 * FALSE for a masked seat. A comparison was made; this caller may not have it.
 * Telling those two apart is the whole job of this function, exactly as
 * {@link classifyPosition} tells `restricted` from `absent`.
 *
 * Nothing here consults `can()`. The server already decided per row per caller
 * (self is unmasked WITHOUT `field.location.view`, so a permission check on the
 * client would be wrong today, not merely someday).
 */
export type AgreementState =
  | { kind: 'agrees' }
  | { kind: 'disagrees' }
  | { kind: 'restricted' }
  | { kind: 'undeterminable' };

export function classifyAgreement(value: ArrivalAgreement | string): AgreementState {
  if (value === MASKED_POSITION) return { kind: 'restricted' };
  if (value === 'agrees') return { kind: 'agrees' };
  if (value === 'disagrees') return { kind: 'disagrees' };
  // 'undeterminable' AND anything unrecognised. A token this client has never
  // heard of is not a determination it may render as one; "no comparison" is
  // the only safe reading of a word we cannot interpret.
  return { kind: 'undeterminable' };
}

function trimDecimal(value: string, places: number): string {
  const dot = value.indexOf('.');
  if (dot === -1) return value;
  return value.slice(0, dot + 1 + places);
}
